import React from "react";
import siteConfig from "../config/siteConfig";
import PolicyPage from "./PolicyPage";
import "../css/privacy.scss";

function Privacy() {
  const sections = [
    {
      id: "store-data",
      title: "How this store handles data",
      paragraphs: [
        "We use the information you provide to manage checkout, fulfil orders and answer customer-care requests.",
        "The store may use local browser storage for a cart or preferences. Clearing site data in your browser removes information kept only on that device.",
      ],
    },
    {
      id: "controller",
      title: "Who would be responsible",
      paragraphs: [
        `${siteConfig.company.legalName}, ${siteConfig.contact.address}, is responsible for personal data collected by the ${siteConfig.brandName} store. Privacy enquiries can be sent to ${siteConfig.contact.email}.`,
      ],
    },
    {
      id: "collection",
      title: "Information a live store may collect",
      paragraphs: [
        "The information depends on how you use the store and the choices you make. A production implementation may collect:",
      ],
      items: [
        "Identity and contact details, such as name, billing and delivery addresses and email.",
        "Order, return and customer-care records, including products, sizes, messages and transaction references.",
        "Payment status and fraud-prevention signals supplied by a payment provider; full card details should be handled by that provider, not stored by the storefront.",
        "Technical and usage information, such as IP address, device, browser, pages viewed, cookie choices and approximate location.",
        "Marketing preferences and interactions with messages where consent or another lawful basis permits measurement.",
      ],
    },
    {
      id: "use",
      title: "Why information may be used",
      paragraphs: [
        "A live store would use data only where there is a lawful basis, including performing an order contract, complying with legal duties, pursuing proportionate legitimate interests or acting on consent.",
      ],
      items: [
        "Process orders, delivery, returns, refunds and customer-care requests.",
        "Secure the service, prevent fraud and diagnose technical problems.",
        "Meet accounting, tax, product-safety and other legal obligations.",
        "Improve products, accessibility and store performance using aggregated insights.",
      ],
    },
    {
      id: "cookies",
      title: "Cookies and similar storage",
      paragraphs: [
        "Essential storage may remember a basket, security state and privacy choices. Analytics or advertising technologies should remain off until valid consent is obtained where required. A production cookie panel must identify each technology, provider, purpose and duration and allow preferences to be changed later.",
      ],
    },
    {
      id: "sharing",
      title: "Service providers and transfers",
      paragraphs: [
        "A live store may share only necessary information with vetted providers for hosting, payments, fraud screening, fulfilment, delivery, support, analytics and professional advice. Data may also be disclosed where law requires it or as part of a properly managed business reorganisation.",
        "When information moves across borders, we use the safeguards required by applicable privacy law and work with providers that protect it appropriately.",
      ],
    },
    {
      id: "retention",
      title: "Retention and security",
      paragraphs: [
        "Personal data should be kept only for the time needed for the stated purpose, including applicable warranty, accounting, tax and dispute periods. Afterwards it should be deleted or irreversibly anonymised.",
        "A production service should combine access controls, encryption, monitoring and staff procedures to protect data. No online service can promise absolute security, so suspected incidents must be assessed and notified as required by law.",
      ],
    },
    {
      id: "rights",
      title: "Your privacy rights",
      paragraphs: [
        `Depending on your location, you may ask to access, correct, erase, restrict or receive your personal data, object to certain uses, withdraw consent, or complain to a supervisory authority. Contact ${siteConfig.contact.email}; identity may need to be verified before a request is fulfilled.`,
      ],
    },
    {
      id: "children",
      title: "Children and policy changes",
      paragraphs: [
        "The store is not directed to children who cannot independently consent to data processing in their jurisdiction. A production store should not knowingly collect their data without appropriate authorisation.",
        "Material privacy changes should be dated and clearly communicated. The review date above identifies this sample version.",
      ],
    },
  ];

  return (
    <div className="privacy">
      <PolicyPage
        eyebrow="Legal"
        title="Privacy notice"
        intro={`How ${siteConfig.brandName} handles personal information and browser storage.`}
        sections={sections}
        relatedLinks={[
          { label: "Terms", to: "/terms" },
          { label: "Contact", to: "/contact" },
        ]}
      />
    </div>
  );
}

export default Privacy;
